import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { firestore } from '@/firebase'
import {
  readBookingTrackingBuffer,
  removeBookingTrackingBufferKeys,
} from '@/components/consultation/bookingTracking/bookingTrackingStorage'
import { getBookingTrackingSessionId } from '@/components/consultation/bookingTracking/bookingTrackingSession'
import type { BookingTrackingEvent } from '@/components/consultation/bookingTracking/bookingTrackingTypes'

/** Session cache: bookingTracking/ui is never deleted during booking, so skip getDoc after first hit. */
const knownExistingConsultations = new Set<string>()

/** @internal test hook */
export function resetBookingTrackingFlushCacheForTests(): void {
  knownExistingConsultations.clear()
}

function omitUndefinedFields<T extends Record<string, unknown>>(value: T): T {
  const out: Record<string, unknown> = {}
  for (const [key, fieldValue] of Object.entries(value)) {
    if (fieldValue !== undefined) {
      out[key] = fieldValue
    }
  }
  return out as T
}

function eventForFirestore(
  event: BookingTrackingEvent,
  sessionId: string,
  clientEventKey: string,
): BookingTrackingEvent {
  const { consultationId: _bufferOnly, ...persisted } = event
  return omitUndefinedFields({
    ...persisted,
    sessionId: persisted.sessionId ?? sessionId,
    clientEventKey,
  })
}

export async function flushBookingTrackingToFirestore(
  consultationId: string,
  keysToFlush: string[],
  sessionId = getBookingTrackingSessionId(),
): Promise<number> {
  if (keysToFlush.length === 0) {
    return 0
  }

  const buffer = readBookingTrackingBuffer(sessionId)
  const changesMap: Record<string, BookingTrackingEvent> = {}
  const updatePayload: Record<string, BookingTrackingEvent> = {}
  const flushedKeys: string[] = []
  for (const key of keysToFlush) {
    const event = buffer.events[key]
    if (event) {
      const persisted = eventForFirestore(event, sessionId, key)
      changesMap[key] = persisted
      // updateDoc treats dotted keys as field paths (merge nested map entries).
      updatePayload[`changes.${key}`] = persisted
      flushedKeys.push(key)
    }
  }
  if (flushedKeys.length === 0) {
    return 0
  }

  const ref = doc(firestore, 'consultations', consultationId, 'bookingTracking', 'ui')
  let exists = knownExistingConsultations.has(consultationId)
  if (!exists) {
    const existing = await getDoc(ref)
    exists = existing.exists()
    if (exists) {
      knownExistingConsultations.add(consultationId)
    }
  }
  if (exists) {
    await updateDoc(ref, updatePayload)
  } else {
    // First write: nested `changes` map (rules require top-level keys.hasOnly(['changes'])).
    // Do not use updateDoc when the doc is missing — rules evaluate `update` only and return
    // permission-denied instead of not-found.
    await setDoc(ref, { changes: changesMap })
    knownExistingConsultations.add(consultationId)
  }

  removeBookingTrackingBufferKeys(flushedKeys, sessionId)
  return flushedKeys.length
}
